import { useQuery } from '@apollo/client';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GetAudienceSharesDocument } from '../../../../graphql/query/audiences/GetAudienceShares.generated.tsx';
import type { GetAudiencesListQuery } from '../../../../graphql/query/audiences/GetAudiencesList.generated.tsx';
import { AllowedOrganizationsContent } from '../../components/AllowedOrganizations/AllowedOrganizationsContent.tsx';
import { useAudienceShareRecipients } from '../../hooks/useAudienceShareRecipients.ts';
import type { AudienceShares } from '../../types.ts';
import { mapAudienceShareToForm } from '../../utils/mapAudienceShareToForm.ts';

type ShareAudienceDialogContentProps = {
  audience: Pick<GetAudiencesListQuery['audience'][number], 'id' | 'name'>;
  toogleConfirmationDisabled: (disabled: boolean) => void;
};

export const ShareAudienceDialogContent = ({
  audience,
  toogleConfirmationDisabled,
}: ShareAudienceDialogContentProps) => {
  const { t } = useTranslation();
  const { reset } = useFormContext<AudienceShares>();
  const {
    data: { audienceSharesByAudience: fetchedAudienceShares = [] } = {},
    loading: sharesLoading,
    error: sharesError,
  } = useQuery(GetAudienceSharesDocument, { variables: { id: audience.id } });

  const { error, loading: recipientsLoading } = useAudienceShareRecipients();

  const observableShares = useWatch<AudienceShares>({ name: 'audience_share' });

  useEffect(() => {
    if (sharesLoading || sharesError) return;

    toogleConfirmationDisabled(false);
    reset({ audience_share: mapAudienceShareToForm(audience.id, fetchedAudienceShares) });
  }, [sharesLoading, sharesError, fetchedAudienceShares, reset, audience, toogleConfirmationDisabled]);

  if (sharesLoading || !observableShares || recipientsLoading) {
    return <Loader />;
  }

  if (sharesError || error) {
    throw new Error(t('An error occurred while fetching audience shares.'));
  }

  return (
    <DialogContent dividers sx={{ pb: 4 }}>
      <Typography variant="body1" gutterBottom>
        {t('You intend to share the below audience with the following publisher(s):')}
      </Typography>
      <Typography
        variant="body1Bold"
        gutterBottom
        sx={{
          mb: 2,
        }}
      >
        {audience.name}
      </Typography>
      <AllowedOrganizationsContent audienceName={audience.name} />
      <Typography
        sx={{
          mt: 3,
        }}
      >
        {t('Do you want to proceed?')}
      </Typography>
    </DialogContent>
  );
};
